import { useEffect, useRef, useState } from "react";
import { Pause, Play, RotateCcw, Wind } from "lucide-react";
import { Button } from "@/components/ui/button";

const GUIDANCE = [
  "Let your eyes stay open if that feels easier. Notice the room around you.",
  "Feel the support beneath your feet or body. There is nothing to achieve.",
  "Allow an easy breath in, then let it leave gently. No holding or forcing.",
  "Return to your natural breathing. Notice one thing you can see or hear.",
];

export function GuidedBreathingTimer({ title = "A moment to breathe", active = true }: { title?: string; active?: boolean }) {
  const [duration, setDuration] = useState(60);
  const [remaining, setRemaining] = useState(60);
  const [running, setRunning] = useState(false);
  const deadline = useRef(0);
  const complete = remaining === 0;
  const guidance = GUIDANCE[Math.min(3, Math.floor(((duration - remaining) / duration) * 4))];

  useEffect(() => {
    if (active || !running) return;
    setRemaining(Math.max(0, Math.ceil((deadline.current - Date.now()) / 1000)));
    setRunning(false);
  }, [active, running]);

  useEffect(() => {
    if (!running) return;
    const tick = () => {
      const next = Math.max(0, Math.ceil((deadline.current - Date.now()) / 1000));
      setRemaining(next);
      if (next === 0) setRunning(false);
    };
    const id = window.setInterval(tick, 200);
    return () => window.clearInterval(id);
  }, [running]);

  const reset = (seconds = duration) => {
    setRunning(false);
    setRemaining(seconds);
  };
  const toggle = () => {
    if (running) {
      setRemaining(Math.max(0, Math.ceil((deadline.current - Date.now()) / 1000)));
      setRunning(false);
    } else {
      const seconds = complete ? duration : remaining;
      setRemaining(seconds);
      deadline.current = Date.now() + seconds * 1000;
      setRunning(true);
    }
  };

  return (
    <section aria-label={title} className="w-full border-y border-border py-4 text-center text-foreground">
      <h3 className="flex items-center justify-center gap-2 text-base font-semibold"><Wind className="h-4 w-4" />{title}</h3>
      <div role="group" aria-label="Practice duration" className="mt-3 flex flex-wrap justify-center gap-2">
        {[30, 60, 120].map((seconds) => (
          <Button key={seconds} type="button" variant="outline" size="sm" aria-pressed={duration === seconds}
            className="rt-choice" disabled={running}
            onClick={() => { setDuration(seconds); reset(seconds); }}>
            {seconds < 60 ? "30 sec" : `${seconds / 60} min`}
          </Button>
        ))}
      </div>
      <div className="mx-auto mt-4 flex h-24 w-24 items-center justify-center rounded-full border-2 border-gold bg-accent">
        <span role="timer" aria-label="Time remaining" className="text-2xl font-semibold tabular-nums">
          {Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, "0")}
        </span>
      </div>
      <p aria-live="polite" className="mx-auto mt-3 min-h-16 max-w-sm text-sm leading-relaxed">
        {complete ? "Your moment is complete. Take your time before continuing." : guidance}
      </p>
      <div className="mt-3 flex flex-wrap justify-center gap-2">
        <Button type="button" onClick={toggle} className="rt-rb-cta">
          {running ? <Pause /> : <Play />}{running ? "Pause" : complete ? "Begin again" : remaining < duration ? "Resume" : "Begin"}
        </Button>
        <Button type="button" variant="outline" size="icon" onClick={() => reset()} aria-label="Reset breathing timer" title="Reset timer"><RotateCcw /></Button>
      </div>
      <p className="mt-3 text-xs leading-relaxed">Optional, with written guidance and no sound. Breathe normally, or focus on the room instead. Stop whenever you need to.</p>
    </section>
  );
}